/**
 * Favorites API functions
 * API calls for managing favorite providers and resources
 */

import { apiClient } from '@/core/api/client';
import type {
  AllFavoritesWithProfessionals,
  FavoriteProvider,
  FavoriteProfessional,
  FavoriteResource,
  AddFavoriteProviderRequest,
  AddFavoriteProfessionalRequest,
  AddFavoriteResourceRequest,
  FavoriteStatusResponse,
  CuratedTeam,
} from '../types';

const BASE_URL = '/care-seekers/favorites';
// Feature 096: role-neutral route — the same endpoint now serves FP/SP too.
const CURATED_URL = '/curated';

/**
 * Get the global bQuest-curated lists (Preferred Providers + Recommended
 * Resources) shown on My Team. Feature 083. Identical for every viewer.
 */
export const getCuratedTeam = async (): Promise<CuratedTeam> => {
  const response = await apiClient.get<CuratedTeam>(CURATED_URL);
  return response.data;
};

/**
 * Get all favorites (professionals and resources)
 * FR-093: System MUST provide a Favorites page with sections for both types
 */
export const getAllFavorites = async (): Promise<AllFavoritesWithProfessionals> => {
  const response = await apiClient.get<AllFavoritesWithProfessionals>(BASE_URL);
  return response.data;
};

/**
 * Get all favorite providers
 * FR-093: System MUST provide a Favorites page with sections for Favorite Providers
 */
export const getFavoriteProviders = async (): Promise<FavoriteProvider[]> => {
  const response = await apiClient.get<FavoriteProvider[]>(`${BASE_URL}/providers`);
  return response.data;
};

/**
 * Get all favorite resources
 * FR-093: System MUST provide a Favorites page with sections for Favorite Resources
 */
export const getFavoriteResources = async (): Promise<FavoriteResource[]> => {
  const response = await apiClient.get<FavoriteResource[]>(`${BASE_URL}/resources`);
  return response.data;
};

/**
 * Add a provider to favorites
 * FR-092: System MUST allow Care Seekers to save favorite providers
 * FR-096: System MUST prevent duplicate favorites
 */
export const addFavoriteProvider = async (
  data: AddFavoriteProviderRequest
): Promise<FavoriteProvider> => {
  const response = await apiClient.post<FavoriteProvider>(
    `${BASE_URL}/providers`,
    data
  );
  return response.data;
};

/**
 * Add a resource to favorites
 * FR-092: System MUST allow Care Seekers to save favorite resources
 * FR-096: System MUST prevent duplicate favorites
 */
export const addFavoriteResource = async (
  data: AddFavoriteResourceRequest
): Promise<FavoriteResource> => {
  const response = await apiClient.post<FavoriteResource>(
    `${BASE_URL}/resources`,
    data
  );
  return response.data;
};

/**
 * Remove a provider from favorites
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */
export const removeFavoriteProvider = async (providerId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/providers/${providerId}`);
};

/**
 * Remove a resource from favorites
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */
export const removeFavoriteResource = async (resourceId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/resources/${resourceId}`);
};

/**
 * Check if a provider is favorited
 * Helper for UI state management
 */
export const checkProviderFavoriteStatus = async (
  providerId: string
): Promise<FavoriteStatusResponse> => {
  const response = await apiClient.get<FavoriteStatusResponse>(
    `${BASE_URL}/providers/${providerId}/status`
  );
  return response.data;
};

/**
 * Check if a resource is favorited
 * Helper for UI state management
 */
export const checkResourceFavoriteStatus = async (
  resourceId: string
): Promise<FavoriteStatusResponse> => {
  const response = await apiClient.get<FavoriteStatusResponse>(
    `${BASE_URL}/resources/${resourceId}/status`
  );
  return response.data;
};

// ============================================================================
// FavoriteProfessional API Functions (User Story 2 - Phase 4)
// Supports both Service Providers AND Financial Advisors
// ============================================================================

/**
 * Get all favorites with professionals (SP + FA)
 * FR-093: System MUST provide a Favorites page with sections for both types
 */
export const getAllFavoritesWithProfessionals = async (): Promise<AllFavoritesWithProfessionals> => {
  const response = await apiClient.get<AllFavoritesWithProfessionals>(BASE_URL);
  return response.data;
};

/**
 * Get all favorite professionals (SPs and FAs)
 * FR-093: System MUST provide a Favorites page with sections for Favorite Professionals
 */
export const getFavoriteProfessionals = async (): Promise<FavoriteProfessional[]> => {
  const response = await apiClient.get<FavoriteProfessional[]>(`${BASE_URL}/professionals`);
  return response.data;
};

/**
 * Add a professional (SP or FA) to favorites
 * FR-FV-002/003: Support favoriting both SPs and FAs
 */
export const addFavoriteProfessional = async (
  data: AddFavoriteProfessionalRequest
): Promise<FavoriteProfessional> => {
  const response = await apiClient.post<FavoriteProfessional>(
    `${BASE_URL}/professionals`,
    data
  );
  return response.data;
};

/**
 * Remove a professional from favorites
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */
export const removeFavoriteProfessional = async (professionalUserId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/professionals/${professionalUserId}`);
};

/**
 * Check if a professional is favorited
 * Helper for UI state management
 */
export const checkProfessionalFavoriteStatus = async (
  professionalUserId: string
): Promise<FavoriteStatusResponse> => {
  const response = await apiClient.get<FavoriteStatusResponse>(
    `${BASE_URL}/professionals/${professionalUserId}/status`
  );
  return response.data;
};
